
import { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { InfoIcon, Link, Loader2 } from "lucide-react";
import { useFairClick } from '@/hooks/useFairClick';
import { AddSiteForm } from './AddSiteForm';
import { SiteVisitCard } from './SiteVisitCard';
import { SitesList } from './SitesList';
import { VisitHistory } from './VisitHistory';
import { CreditStats } from './CreditStats';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Link as RouterLink } from 'react-router-dom';

export const FairClickContainer = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("visit");
  
  const {
    isLoading,
    userSites,
    userCredits,
    currentSite,
    visitHistory,
    visitLoading,
    getRandomSite,
    addSite,
    deleteSite,
    addCreditsToSite,
    validateVisit,
    refreshData
  } = useFairClick();
  
  // Récupérer un site à visiter au démarrage
  useEffect(() => {
    if (user && !currentSite && !visitLoading) {
      getRandomSite();
    }
  }, [user]);
  
  if (!user) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader>
          <CardTitle>Connexion requise</CardTitle>
          <CardDescription>
            Vous devez être connecté pour utiliser FairClick
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          <p className="text-center">
            Pour participer à l'échange de trafic éthique, veuillez vous connecter ou créer un compte.
          </p>
          <Button asChild>
            <RouterLink to="/auth">Se connecter</RouterLink>
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="w-full space-y-6">
      <div className="grid grid-cols-1 gap-6">
        <Alert>
          <InfoIcon className="h-4 w-4" />
          <AlertTitle>Comment fonctionne FairClick ?</AlertTitle>
          <AlertDescription>
            FairClick est un système d'échange de trafic éthique. Visitez les sites d'autres utilisateurs pendant au moins 10 secondes pour gagner des crédits. Utilisez ces crédits pour recevoir des visites sur vos propres sites.
          </AlertDescription>
        </Alert>
        
        <CreditStats userCredits={userCredits} />
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="visit" className="flex-1">Visiter un site</TabsTrigger>
          <TabsTrigger value="mysites" className="flex-1">Mes sites ({userSites.length})</TabsTrigger>
          <TabsTrigger value="history" className="flex-1">Historique</TabsTrigger>
        </TabsList>
        
        <TabsContent value="visit" className="mt-6">
          <SiteVisitCard 
            site={currentSite}
            onVisitComplete={validateVisit}
            onGetNewSite={getRandomSite}
            isLoading={visitLoading}
          />
        </TabsContent>
        
        <TabsContent value="mysites" className="mt-6 space-y-6">
          <AddSiteForm 
            onAddSite={addSite}
            userCredits={userCredits?.credits || 0}
          />
          
          <SitesList 
            sites={userSites}
            userCredits={userCredits?.credits || 0}
            onAddCredits={addCreditsToSite}
            onDeleteSite={deleteSite}
          />
        </TabsContent>
        
        <TabsContent value="history" className="mt-6">
          <VisitHistory visits={visitHistory} />
        </TabsContent>
      </Tabs>
    </div>
  );
};
